import { useVisitForm } from "@/components/onboarding/VisitFormContext";
import { useState, useEffect, useRef } from "react";
import { useContactForm } from "@/components/onboarding/ContactFormContext";
import { divisionFromPath } from "@/lib/leadGuard";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Menu, X } from "lucide-react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { divisionIcons } from "@/components/landing/DivisionIcons";
import wordmarkBlack from "@/assets/techtail-wordmark-black.png.asset.json";
import wordmarkWhite from "@/assets/techtail-wordmark-pixel.png";
import { withLang, stripLang } from "@/lib/langPath";



type Accent = "retail" | "industrial" | "medical";

interface PanelGroup {
  label: string;
  items: { text: string; href?: string }[];
}

interface NavVertical {
  key: Accent;
  label: string;
  href: string;
  headline: string;
  groups: PanelGroup[];
  highlight?: { text: string; href: string };
}

const buildVerticals = (t: TFunction): NavVertical[] => [
  {
    key: "retail",
    label: t("sChrome.header.retail.label"),
    href: "/retail",
    headline: t("sChrome.header.retail.headline"),
    groups: [
      {
        label: t("sChrome.header.retail.formatsLabel"),
        items: [
          { text: "Flex Store", href: "/formatos/flexstore" },
          { text: "Full Store", href: "/formatos/fullstore" },
          { text: "Corner Store", href: "/formatos/corner" },
          { text: "Cabinet", href: "/formatos/cabinet" },
        ],
      },
      {
        label: t("sChrome.header.retail.sectorsLabel"),
        items: [
          { text: t("sChrome.header.retail.sectors.gasStations"), href: "/retail/gasolineras" },
          { text: t("sChrome.header.retail.sectors.hotels"), href: "/retail/hoteles" },
          { text: t("sChrome.header.retail.sectors.gyms"), href: "/retail/gimnasios" },
          { text: t("sChrome.header.retail.sectors.coworkings"), href: "/retail/coworkings" },
          { text: t("sChrome.header.retail.sectors.campus"), href: "/retail/campus" },
        ],
      },
      {
        label: t("sChrome.header.retail.caseLabel"),
        items: [{ text: "Big Fish", href: "/casos-de-uso" }],
      },
    ],
    highlight: { text: t("sChrome.header.retail.highlight"), href: "/contacto" },
  },
  {
    key: "industrial",
    label: t("sChrome.header.industrial.label"),
    href: "/industrial",
    headline: t("sChrome.header.industrial.headline"),
    groups: [
      {
        label: t("sChrome.header.industrial.useCasesLabel"),
        items: [
          { text: t("sChrome.header.industrial.useCases.mro") },
          { text: t("sChrome.header.industrial.useCases.tool") },
          { text: t("sChrome.header.industrial.useCases.spareParts") },
          { text: t("sChrome.header.industrial.useCases.ppe") },
          { text: t("sChrome.header.industrial.useCases.lineSupply") },
        ],
      },
      {
        label: t("sChrome.header.industrial.sectorsLabel"),
        items: [
          { text: t("sChrome.header.industrial.sectors.automotive"), href: "/industrial/automocion" },
          { text: t("sChrome.header.industrial.sectors.food"), href: "/industrial/alimentacion" },
          { text: t("sChrome.header.industrial.sectors.metallurgy"), href: "/industrial/metalurgia" },
          { text: t("sChrome.header.industrial.sectors.chemical"), href: "/industrial/quimica" },
          { text: t("sChrome.header.industrial.sectors.energy"), href: "/industrial/energia" },
        ],
      },

      {
        label: t("sChrome.header.industrial.integrationsLabel"),
        items: [
          { text: t("sChrome.header.industrial.integrations.sap") },
          { text: t("sChrome.header.industrial.integrations.sage") },
          { text: t("sChrome.header.industrial.integrations.more") },
        ],
      },
    ],
  },
  {
    key: "medical",
    label: t("sChrome.header.medical.label"),
    href: "/medical",
    headline: t("sChrome.header.medical.headline"),
    groups: [
      {
        label: t("sChrome.header.medical.traceabilityLabel"),
        items: [
          { text: t("sChrome.header.medical.traceability.byBatch") },
          { text: t("sChrome.header.medical.traceability.byExpiry") },
        ],
      },
      {
        label: t("sChrome.header.medical.consumptionLabel"),
        items: [
          { text: t("sChrome.header.medical.consumption.byService") },
          { text: t("sChrome.header.medical.consumption.byProcedure") },
        ],
      },
      {
        label: t("sChrome.header.medical.integrationLabel"),
        items: [{ text: t("sChrome.header.medical.integration.his") }],
      },
    ],
  },
];

const buildNavSublinks = (t: TFunction): Record<Accent, { text: string; href: string }[]> => ({
  retail: [
    { text: t("sChrome.header.retail.sublinkFormats"), href: "/retail#formatos" },
    { text: t("sChrome.header.retail.sublinkSectors"), href: "/retail#sectores" },
    { text: t("sChrome.header.retail.sublinkHowItWorks"), href: "/retail#como-funciona" },
  ],
  industrial: [
    { text: t("sChrome.header.industrial.sublinkUseCases"), href: "/industrial#casos-de-uso" },
    { text: t("sChrome.header.industrial.sublinkSectors"), href: "/industrial#industrias" },
    { text: t("sChrome.header.industrial.sublinkHowItWorks"), href: "/industrial#como-funciona" },
  ],

  medical: [{ text: t("sChrome.header.medical.sublinkDiscover"), href: "/medical" }],
});

const accentHoverClass: Record<Accent, string> = {
  retail: "group-hover/nav:text-white",
  industrial: "group-hover/nav:text-white",
  medical: "group-hover/nav:text-white",
};


const accentClass: Record<Accent, string> = {
  retail: "text-retail",
  industrial: "text-industrial",
  medical: "text-medical",
};

const Header = () => {
  const { open: openVisit } = useVisitForm();
  const { open: openContact } = useContactForm();
  const { i18n, t } = useTranslation();
  const verticals = buildVerticals(t);
  const navSublinks = buildNavSublinks(t);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openPanel, setOpenPanel] = useState<Accent | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [onBlue, setOnBlue] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      if (y > 140 && y > lastY.current + 6) setHidden(true);
      else if (y < lastY.current - 6 || y <= 140) setHidden(false);
      lastY.current = y;
      const zones = document.querySelectorAll<HTMLElement>('[data-header-theme="blue"]');
      let overlapping = false;
      const probe = 60; // punto medio del header
      zones.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top <= probe && r.bottom >= probe) overlapping = true;
      });
      setOnBlue(overlapping);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [location.pathname]);



  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenPanel(null);
  }, [location.pathname, location.hash, location.key]);

  useEffect(() => {
    document.body.style.overflow = "";
  }, [isMobileMenuOpen]);

  // Dedicated portal host so the overlay is always removed with this header
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const el = document.createElement("div");
    document.body.appendChild(el);
    setPortalHost(el);
    return () => {
      el.remove();
    };
  }, []);




  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenPanel(null), 140);
  };
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const active = verticals.find((v) => v.key === openPanel);
  const isRetail = stripLang(location.pathname).startsWith("/retail");
  const isMedical = stripLang(location.pathname).startsWith("/medical");
  const isIndustrial = stripLang(location.pathname).startsWith("/industrial");
  const isCompany = stripLang(location.pathname).startsWith("/compania");
  // Header sólido: azul en home/compañía, rosa en retail, verde en medical.
  // Páginas de fondo claro => tinta negra también al hacer scroll.
  const isLightBg = false;
  const light = false;
  const solid = !isScrolled || isLightBg;
  const ink = isLightBg || !isScrolled ? "text-black" : "text-bone";
  const inkBg = isLightBg || !isScrolled ? "bg-black" : "bg-bone";


  const accentBg = isRetail
    ? "bg-retail"
    : isMedical
      ? "bg-medical"
      : isIndustrial
        ? "bg-industrial"
        : "bg-brand-blue";
  const accentBgSoft = isRetail
    ? "bg-retail/70"
    : isMedical
      ? "bg-medical/70"
      : isIndustrial
        ? "bg-industrial/70"
        : "bg-brand-blue/70";

  const baseBg = isScrolled ? `${accentBgSoft} backdrop-blur-md` : accentBg;


  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      onMouseLeave={scheduleClose}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${baseBg}`}
    >


      <div className="container mx-auto px-4 md:px-6 relative z-50">
        <div className="flex items-start justify-between py-3 md:py-5 min-h-[72px] lg:min-h-[120px]">
          {/* Left — wordmark */}
          <Link to={withLang("/")} className="shrink-0 pt-1" aria-label="TECHTAIL">
            <img
              src={solid ? wordmarkBlack.url : wordmarkWhite}
              alt="TECHTAIL"
              className="h-9 md:h-8 w-[180px] md:w-[160px] object-contain object-left"
            />
          </Link>


          {/* Center — big icon nav (Teenage Engineering style) */}
          <nav className="group/menu hidden lg:flex items-start gap-10">
            {verticals.map((v) => {
              const Icon = divisionIcons[v.key];
              const sublinks = navSublinks[v.key];
              return (
                <div key={v.key} className={`group/nav flex items-start gap-3 ${ink} transition-opacity duration-0 opacity-100 group-hover/menu:opacity-40 hover:!opacity-100`}>
                  <Link to={withLang(v.href)} aria-label={v.label}>
                    <Icon className="h-20 w-20 shrink-0" />
                  </Link>

                  <div className="flex flex-col justify-between h-20">
                    <Link to={withLang(v.href)} className="font-pixel font-light text-2xl leading-none">
                      {v.label}
                    </Link>
                    <div className="flex flex-col mt-2">
                      {sublinks.map((s) =>
                        s.href ? (
                          <Link key={s.text} to={withLang(s.href)} className="block text-sm leading-snug no-underline hover:underline underline-offset-2 hover:opacity-70 transition-opacity">
                            {s.text}
                          </Link>
                        ) : (
                          <span key={s.text} className="block text-sm leading-snug">{s.text}</span>
                        )
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            <div className={`group/col flex flex-col justify-end h-20 ${ink} transition-all duration-0 opacity-100 group-hover/menu:opacity-40 hover:!opacity-100`}>
              <Link to={withLang("/recursos")} className="block text-xs leading-tight opacity-100 group-hover/col:opacity-40 hover:!opacity-100 transition-opacity">
                {t("sContent.recursos.label")}
              </Link>
              <Link to={withLang("/casos")} className="block text-xs leading-tight opacity-100 group-hover/col:opacity-40 hover:!opacity-100 transition-opacity">
                {t("sContent.casos.label")}
              </Link>
              <Link to={withLang("/compania")} className="block text-xs leading-tight opacity-100 group-hover/col:opacity-40 hover:!opacity-100 transition-opacity">
                {t("sChrome.header.company")}
              </Link>
              <button type="button" onClick={openContact} className="block text-left text-xs leading-tight opacity-100 group-hover/col:opacity-40 hover:!opacity-100 transition-opacity">
                {t("sChrome.header.contact")}
              </button>
            </div>



          </nav>

          {/* Right */}
          <div className="hidden md:flex items-center gap-4 pt-1">
            <div className={`relative ${ink}`}>
              <select
                value={["es","en","it","de","fr"].find((c) => i18n.language?.startsWith(c)) || "es"}
                onChange={(e) => i18n.changeLanguage(e.target.value)}
                aria-label="Language"
                className={`appearance-none bg-transparent border ${isLightBg || !isScrolled ? "border-black/30" : "border-white/40"} rounded-none pl-2 pr-6 py-1 text-xs uppercase tracking-widest cursor-pointer focus:outline-none ${ink}`}
              >
                {["es", "en", "it", "de", "fr"].map((code) => (
                  <option key={code} value={code} className="text-black">
                    {code.toUpperCase()}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[9px]">▼</span>
            </div>

            <button
              type="button"
              onClick={() => navigate(withLang("/estudio-implantacion"))}
              className={`group/cta inline-flex items-center gap-2.5 text-base md:text-lg ${ink} transition-colors`}
            >
              <span className={`h-3 w-3 ${inkBg} group-hover/cta:w-7 group-active/cta:w-8 transition-all duration-150 ease-out`} />
              <span className="underline underline-offset-4">{t("sChrome.header.studyCta")}</span>
            </button>

          </div>

          <button
            className={`lg:hidden p-2 ${ink}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={t("sChrome.header.menuAria")}
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>


      {/* Mega panel */}
      <AnimatePresence>
        {active && (
          <motion.div
            key={active.key}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            className="hidden lg:block absolute left-0 right-0 top-full bg-background border-b border-border"
          >
            <div className="container mx-auto px-4 md:px-6 py-10">
              <div className="grid grid-cols-12 gap-8">
                <div className="col-span-3">
                  {(() => {
                    const Icon = divisionIcons[active.key];
                    return <Icon className={`h-12 w-12 mb-4 ${accentClass[active.key]}`} />;
                  })()}
                  <Link
                    to={withLang(active.href)}
                    className={`font-pixel text-2xl ${accentClass[active.key]} hover:opacity-80 transition-opacity`}
                  >
                    {active.headline}
                  </Link>
                  <p className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">
                    {active.label}
                  </p>
                </div>

                {active.groups.map((g) => (
                  <div key={g.label} className="col-span-3">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-4">
                      {g.label}
                    </p>
                    <ul className="space-y-2">
                      {g.items.map((it) => (
                        <li key={it.text}>
                          {it.href ? (
                            <Link
                              to={withLang(it.href)}
                              className="text-sm text-foreground/80 hover:text-foreground transition-colors"
                            >
                              {it.text}
                            </Link>
                          ) : (
                            <span className="text-sm text-foreground/60">{it.text}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              {active.highlight && (
                <div className="flex justify-end mt-8 pt-6 border-t border-border">
                  <Link
                    to={withLang(active.highlight.href)}
                    className={`group inline-flex items-center gap-3 text-sm font-medium uppercase tracking-widest ${accentClass[active.key]}`}
                  >
                    {active.highlight.text}
                    <span className="font-pixel group-hover:translate-x-1 transition-transform">→</span>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile — full screen menu */}
      {portalHost &&
        createPortal(
          <>
          <div
            aria-hidden
            onClick={() => setIsMobileMenuOpen(false)}
            className={`lg:hidden fixed inset-0 z-[59] bg-black/30 transition-opacity duration-300 ${
              isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          />
          <nav
            aria-hidden={!isMobileMenuOpen}
            onClickCapture={(e) => {
              const el = e.target as HTMLElement;
              if (el.closest("a,button")) setIsMobileMenuOpen(false);
            }}
            className={`lg:hidden fixed inset-y-0 right-0 left-14 z-[60] bg-black flex flex-col px-6 pt-24 pb-10 overflow-y-auto transition-transform duration-300 ease-out ${
              isMobileMenuOpen ? "translate-x-0" : "translate-x-full pointer-events-none"
            }`}
          >

                <div className="flex flex-col items-start flex-1 justify-center">
                  {verticals.map((v) => (
                    <motion.div key={v.key} whileHover={{ scale: 1.06, x: 8 }} whileTap={{ scale: 0.96 }} transition={{ type: "spring", stiffness: 400, damping: 18 }} className="origin-left">
                      <Link
                        to={withLang(v.href)}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block font-serif font-normal text-5xl leading-[1.05] tracking-[-0.01em] py-3 text-bone transition-colors hover:text-brand-blue"
                      >
                        {v.label}
                      </Link>
                    </motion.div>
                  ))}
                  <motion.div whileHover={{ scale: 1.06, x: 8 }} whileTap={{ scale: 0.96 }} transition={{ type: "spring", stiffness: 400, damping: 18 }} className="origin-left">
                    <Link
                      to={withLang("/compania")}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block font-serif font-normal text-3xl leading-tight py-2 text-bone/90 transition-colors hover:text-brand-blue"
                    >
                      {t("sChrome.header.company")}
                    </Link>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.06, x: 8 }} whileTap={{ scale: 0.96 }} transition={{ type: "spring", stiffness: 400, damping: 18 }} className="origin-left">
                    <button
                      type="button"
                      onClick={() => { setIsMobileMenuOpen(false); openContact(); }}
                      className="font-serif font-normal text-3xl leading-tight py-2 text-left text-bone/90 transition-colors hover:text-brand-blue"
                    >
                      {t("sChrome.header.contact")}
                    </button>
                  </motion.div>
                </div>

                <div className="pt-6 mt-6 border-t border-white/20 flex items-center gap-3">
                  <select
                    value={["es","en","it","de","fr"].find((c) => i18n.language?.startsWith(c)) || "es"}
                    onChange={(e) => i18n.changeLanguage(e.target.value)}
                    aria-label="Language"
                    className="bg-transparent border border-white/40 rounded-none px-3 py-2 text-xs uppercase tracking-widest text-bone"
                  >
                    {["es", "en", "it", "de", "fr"].map((code) => (
                      <option key={code} value={code} className="text-black">
                        {code.toUpperCase()}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => { setIsMobileMenuOpen(false); navigate(withLang("/estudio-implantacion")); }}
                    className="flex-1 rounded-none bg-brand-blue text-bone px-5 py-3 font-serif text-base"
                  >
                    {t("sChrome.header.studyCta")}
                  </button>
                </div>

                <div className="flex justify-center pt-8">
                  <button
                    type="button"
                    aria-label="Close menu"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="h-12 w-12 flex items-center justify-center border border-white/30 text-bone hover:text-brand-blue transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
          </nav>
          </>,

          portalHost
        )}



    </motion.header>
  );
};

export default Header;
